import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { isEnterPressed, isEscapePressed } from 'utils';
import { Input } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropzoneFile } from 'components/Dropzone/utils';
import MymeTypeIcon from '../../../MymeTypeIcon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomSelectedDocumentsContext from '../../../../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import useRenameFile from '../../../../hooks/useRenameFile';

import './EditableDocument.styles.scss';

const INPUT_PADDING = 6;

type EditableDocumentProps = {
  className?: string;
  name: string;
  id: string;
  document?: DropzoneFile;
  inputPadding?: number;
  setLoading?: (value: boolean) => void;
  onCloseEdit?: () => void;
  /**
   * Optional rename override. When provided it is used instead of the default
   * `useRenameFile` flow (which relies on the Documents/Selected contexts that
   * don't exist outside the Files page, e.g. in search results).
   */
  onRename?: (values: { name: string }) => Promise<void>;
  /** Optional selection reset override for the same reason as `onRename`. */
  onRemoveSelection?: () => void;
};

const EditableDocument: FC<EditableDocumentProps> = ({
  id,
  name,
  document,
  setLoading,
  onCloseEdit,
  onRename,
  onRemoveSelection,
  inputPadding = INPUT_PADDING,
}) => {
  const [inputValue, setInputValue] = useState(name);

  const [input, inputRef] = useCallbackRef();
  const [span, spanRef] = useCallbackRef();

  const { popServerError } = useContext(PopNotificationsContext);
  const { removeAll } = useContext(DataRoomSelectedDocumentsContext);

  const defaultRenameFile = useRenameFile({ id });

  const renameFile = onRename ?? defaultRenameFile;
  const removeSelection = onRemoveSelection ?? removeAll;

  const handleOnClose = useCallback(() => {
    setInputValue(name);
    onCloseEdit?.();
  }, [name, onCloseEdit]);

  const handleOnEdit = useCallback(async () => {
    if (name === inputValue) {
      handleOnClose();
      return;
    }

    try {
      setLoading?.(true);
      await renameFile({ name: inputValue });
      onCloseEdit?.();
    } catch (e) {
      popServerError(e);
    } finally {
      setLoading?.(false);
    }
  }, [
    name,
    inputValue,
    handleOnClose,
    setLoading,
    renameFile,
    onCloseEdit,
    popServerError,
  ]);

  const handleOnKeyDown = useCallback(
    (e) => {
      if (isEnterPressed(e)) {
        handleOnEdit();
      } else if (isEscapePressed(e)) {
        handleOnClose();
      }
    },
    [handleOnClose, handleOnEdit],
  );

  const handleClickedOutside = useCallback(
    (event: MouseEvent) => {
      if (input && !input?.contains(event.target)) {
        handleOnClose();
      }
    },
    [input, handleOnClose],
  );

  useEffect(() => {
    window.document.addEventListener('click', handleClickedOutside);

    return () => {
      window.document.removeEventListener('click', handleClickedOutside);
    };
  }, [handleClickedOutside]);

  useEffect(() => {
    if (span && input) {
      input.style.width = `${span.offsetWidth + 2 * inputPadding}px`;
    }
  }, [input, inputPadding, inputValue, span]);

  useEffect(() => {
    if (input) {
      input.focus();
      input.select();
      removeSelection();
    }
  }, [input, removeSelection]);

  return (
    <div className="editable-document">
      <MymeTypeIcon mimeType={document?.originalType || document?.type} />

      <Input
        value={inputValue}
        autoFocus
        ref={inputRef}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleOnKeyDown}
      />

      <span ref={spanRef} className="editable-document__span">
        {inputValue}
      </span>
    </div>
  );
};

export default EditableDocument;
